import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';

/**
 * Modalità demo pubblica: l'utente arriva dal sito (rotta /demo) e usa la
 * piattaforma con dati simulati SENZA login e SENZA organization reale.
 *
 * Questo servizio è l'unica fonte di verità su "siamo in demo pubblica".
 * Da non confondere con:
 *   - `participant.isDemo`      → questo singolo veicolo è finto
 *   - `organization.login_view_demo` → account loggato senza tracker attivi
 * Sono tre concetti diversi che prima erano mescolati.
 *
 * Lo stato è persistito in localStorage (non in @ionic/storage) perché va
 * letto in modo sincrono all'avvio, prima che il gate di autenticazione in
 * app.component decida dove mandare l'utente.
 */
@Injectable({
  providedIn: 'root'
})
export class DemoService {

  static readonly STORAGE_KEY = 'demo_session';

  /** True se la sessione demo è attiva. Statico come AuthService.isLogged:
   *  va letto anche da pagine e guard che non iniettano questo servizio. */
  static active: boolean = false;

  /** Avvisa che la demo pubblica è finita.
   *  Serve perché all'avvio la demo risulta attiva (ripristinata dallo storage)
   *  mentre Firebase non ha ancora detto se l'utente è entrato: chat e giro
   *  guidato partono, e quando poi si scopre che c'è un account vero nessuno
   *  li spegnerebbe. Succede a chi si registra dallo stepper arrivandoci dalla
   *  demo e torna subito nel portale. */
  static readonly chiusa: Subject<void> = new Subject<void>();

  /** Identificativo del browser generato dal sito, propagato fino alla registrazione. */
  idBrowser: string;

  /** Timestamp (ms) di avvio della sessione demo. */
  timestampStarted: number;

  /**
   * L'inizializzazione avviene qui e non nel bootstrap di app.component perché
   * il Router parte prima: risolve la rotta ed esegue i guard mentre
   * initializeApp è ancora dietro platform.ready() e alla getConfigInfo. Il
   * guard però inietta questo servizio, quindi al momento in cui serve il
   * singleton è già costruito e lo stato demo è corretto.
   *
   * `environment.originalUrl` è valutato al caricamento del modulo, quindi è
   * l'URL di ingresso reale, non quello già riscritto da eventuali redirect.
   */
  constructor() {
    if(Capacitor.isNativePlatform()) return;   // sull'app nativa la demo non esiste
    this.initFromUrl(environment.originalUrl);
  }

  /**
   * Avvia la sessione demo e la persiste. Se non arriva un idBrowser dal sito
   * ne generiamo uno: serve comunque a legare la visita demo all'eventuale
   * registrazione (richiesta contatto) e alla conversazione del bot.
   */
  start(idBrowser?: string, operatore?: string){
    DemoService.active = true;
    this.idBrowser = idBrowser || this.generateIdBrowser();
    this.timestampStarted = Date.now();
    this.save();
    this.condividiConIlSito(operatore);
  }

  /**
   * La demo gira su web.balin.app, la landing su balin.app: sono due origini
   * diverse e il localStorage non passa. Quello che il sito ci manda nell'URL
   * lo riscriviamo qui nelle stesse chiavi che usa lui, così la chat
   * dell'assistente ritrova la conversazione di prima e la stessa persona.
   */
  private condividiConIlSito(operatore?: string){
    try{
      if(this.idBrowser) localStorage.setItem('id_browser', this.idBrowser);
      if(operatore) localStorage.setItem('balin_operatore', operatore);
    }catch(err){
      console.error('Errore in condivisione dati con il sito', err);
    }
  }

  /**
   * "Registrati" dalla demo: la chiude e riparte dal login, già sulla scheda
   * di registrazione, con l'id del visitatore per legare l'account alla visita.
   *
   * Si ricarica la pagina di proposito invece di navigare dentro l'app: i dati
   * finti della demo restano in memoria finché l'app non riparte, e con la
   * sessione tolta dallo storage all'avvio non viene più ripristinata.
   * Non si usa ?isDemo=true, che sulla pagina di login riaprirebbe la demo.
   */
  esciERegistrati(){
    const id: string = this.idBrowser;
    this.stop();
    window.location.href = '/login?page=signUp' + (id ? '&idBrowser=' + encodeURIComponent(id) : '');
  }

  /**
   * Il clic dell'annuncio Google con cui il visitatore era arrivato sul sito: la
   * landing lo passa nell'indirizzo (gclid, oppure gbraid/wbraid dai clic da
   * iPhone, e tclic con l'ora del clic). Si salva con la stessa chiave e lo
   * stesso formato che legge lo stepper della prova (assets/prova/prova.js):
   * chi dalla demo passa alla richiesta se lo porta dietro, e la richiesta
   * resta legata all'annuncio. Non si cancella con la fine della demo.
   */
  private salvaClicAnnuncio(params: URLSearchParams){
    try{
      const tipo: string = ['gclid', 'gbraid', 'wbraid'].find(k => params.get(k));
      if(!tipo) return;
      localStorage.setItem('ads_clic', JSON.stringify({
        v: params.get(tipo).slice(0, 200),
        t: Number(params.get('tclic')) || Date.now(),
        url: String(window.location.href).slice(0, 500),
        tipo
      }));
    }catch(err){
      console.error('Errore in salvataggio clic annuncio', err);
    }
  }

  /**
   * I parametri dell'annuncio (utm_*, campaign, keyword…) che la landing passa
   * insieme al clic: tutti quelli dell'indirizzo tranne i nostri, salvati come
   * li legge lo stepper (chiave 'ads_parametri'). Finiscono negli ads_params
   * della richiesta di prova, da cui l'admin prende campagna e parola chiave.
   */
  private salvaParametriAnnuncio(params: URLSearchParams){
    try{
      const nostri: string[] = ['idBrowser', 'operatore', 'tclic', 'isDemo', 'num_vehicles'];
      const trovati: any = {};
      let quanti = 0;
      params.forEach((valore: string, nome: string) => {
        if(nostri.includes(nome) || quanti >= 25) return;
        trovati[nome.slice(0, 50)] = String(valore).slice(0, 200);
        quanti++;
      });
      if(quanti) localStorage.setItem('ads_parametri', JSON.stringify(trovati));
    }catch(err){
      console.error('Errore in salvataggio parametri annuncio', err);
    }
  }

  /** Chiude la sessione demo e ripulisce lo storage. */
  stop(){
    DemoService.active = false;
    this.idBrowser = undefined;
    this.timestampStarted = undefined;
    try{
      localStorage.removeItem(DemoService.STORAGE_KEY);
    }catch(err){
      console.error('Errore in rimozione sessione demo', err);
    }
    DemoService.chiusa.next();
  }

  /**
   * Punto di ingresso unico al bootstrap: decide se siamo in demo guardando
   * prima lo storage e poi l'URL di ingresso. Ritorna true se la demo è attiva.
   *
   * La porta della demo e' una sola, /demo: la usano sia il sito (openDemo in
   * js/common.js) sia lo stepper della prova. La vecchia forma ?isDemo=true
   * resta riconosciuta per i link gia' in giro (email, campagne, segnalibri),
   * ma non la scrive piu' nessuno.
   * L'idBrowser che arriva dall'URL ha la precedenza su quello già in sessione;
   * se non arriva teniamo quello che avevamo, così un reload di /demo non
   * spezza il collegamento con la visita originale.
   */
  initFromUrl(url: any): boolean {
    const restored: boolean = this.restore();
    const fromPath: boolean = url && url.pathname === '/demo';
    const fromQuery: boolean = url && url.searchParams && url.searchParams.get('isDemo') === 'true';
    if(fromPath || fromQuery){
      const idBrowser: string = url.searchParams ? url.searchParams.get('idBrowser') : undefined;
      const operatore: string = url.searchParams ? url.searchParams.get('operatore') : undefined;
      this.start(idBrowser || this.idBrowser, operatore);
      if(url.searchParams){
        this.salvaClicAnnuncio(url.searchParams);
        this.salvaParametriAnnuncio(url.searchParams);
      }
      return true;
    }
    return restored;
  }

  /**
   * Ripristina la sessione demo da localStorage. Va chiamato all'avvio, prima
   * del gate di autenticazione. Ritorna true se la demo risulta attiva.
   */
  restore(): boolean {
    try{
      const raw: string = localStorage.getItem(DemoService.STORAGE_KEY);
      if(!raw) return false;
      const session: any = JSON.parse(raw);
      if(!session || !session.active) return false;
      DemoService.active = true;
      this.idBrowser = session.idBrowser;
      this.timestampStarted = session.timestampStarted;
      return true;
    }catch(err){
      console.error('Errore in ripristino sessione demo', err);
      return false;
    }
  }

  private save(){
    try{
      localStorage.setItem(DemoService.STORAGE_KEY, JSON.stringify({
        active: DemoService.active,
        idBrowser: this.idBrowser,
        timestampStarted: this.timestampStarted
      }));
    }catch(err){
      console.error('Errore in salvataggio sessione demo', err);
    }
  }

  /** Stesso formato generato dal sito in js/common.js, per coerenza dei lead. */
  private generateIdBrowser(): string {
    return Date.now() + (Math.random() + 1).toString(36).substring(7);
  }
}
